/**
 * Navbar — fixed top, glass with subtle blur, scroll-reactive border.
 */
import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Menu, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import outlappedWordmark from "../assets/outlapped-wordmark.svg";

const NAV_ITEMS = [
    { to: "/replay", label: "Replay" },
    { to: "/calendar", label: "Calendar" },
    {
        label: "Grid",
        items: [
            { to: "/drivers", label: "Drivers" },
            { to: "/teams", label: "Teams" },
            { to: "/standings", label: "Stats" },
        ],
    },
    { to: "/news", label: "Briefing" },
    { to: "/learn", label: "Learn F1" },
];

const LIVE_ROUTE = "/race/live";

function isNavGroupActive(pathname, item) {
    if (!item.items) return false;
    return item.items.some((child) => pathname === child.to || pathname.startsWith(`${child.to}/`));
}

function slugify(label) {
    return label.toLowerCase().replace(/\s+/g, "-");
}

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    return (
        <>
            <motion.nav
                initial={{ y: -20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                data-testid="navbar"
                className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
                    scrolled
                        ? "glass-strong border-b border-white/5"
                        : "bg-transparent"
                }`}
            >
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-[84px] flex items-center justify-between">
                    <Link
                        to="/"
                        data-testid="nav-logo"
                        className="flex items-center group"
                    >
                        <img
                            src={outlappedWordmark}
                            alt="Outlapped"
                            className="h-9 w-auto sm:h-10 transition-transform duration-300 group-hover:scale-[1.03]"
                        />
                    </Link>

                    <div className="hidden lg:flex h-full items-center gap-1">
                        {NAV_ITEMS.map((item) =>
                            item.items ? (
                                <NavDropdown
                                    key={item.label}
                                    item={item}
                                    active={isNavGroupActive(location.pathname, item)}
                                />
                            ) : (
                                <NavLink
                                    key={item.to}
                                    to={item.to}
                                    data-testid={`nav-${slugify(item.label)}`}
                                    className={({ isActive }) =>
                                        `relative inline-flex h-full items-center px-3.5 text-[13px] font-medium tracking-wide transition-colors ${
                                            isActive
                                                ? "text-white"
                                                : "text-zinc-400 hover:text-white"
                                        }`
                                    }
                                >
                                    {({ isActive }) => (
                                        <span className="relative inline-flex items-center">
                                            {item.label}
                                            {isActive && (
                                                <motion.div
                                                    layoutId="nav-underline"
                                                    className="absolute -bottom-1 left-0 right-0 h-[2px] bg-f1-red"
                                                />
                                            )}
                                        </span>
                                    )}
                                </NavLink>
                            )
                        )}
                        <OpenDashboardButton />
                    </div>

                    <div className="hidden lg:flex h-full items-center gap-2">
                        <AuthButtons />
                    </div>

                    <button
                        data-testid="mobile-menu-toggle"
                        onClick={() => setOpen((v) => !v)}
                        className="lg:hidden h-10 w-10 grid place-items-center rounded-md hover:bg-white/5"
                        aria-label="Toggle menu"
                    >
                        {open ? <X size={20} /> : <Menu size={20} />}
                    </button>
                </div>
            </motion.nav>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.2 }}
                        className="fixed inset-x-0 top-[84px] z-40 glass-strong border-b border-white/10 lg:hidden"
                        data-testid="mobile-menu"
                    >
                        <div className="px-6 py-6 flex flex-col gap-1">
                            {NAV_ITEMS.map((item) => (
                                item.items ? (
                                    <div key={item.label} className="py-2">
                                        <div
                                            className={`font-mono text-[10px] uppercase tracking-[0.22em] ${
                                                isNavGroupActive(location.pathname, item)
                                                    ? "text-f1-red"
                                                    : "text-zinc-500"
                                            }`}
                                        >
                                            {item.label}
                                        </div>
                                        <div className="mt-2 flex flex-col border-l border-white/10 pl-4">
                                            {item.items.map((child) => (
                                                <NavLink
                                                    key={child.to}
                                                    to={child.to}
                                                    data-testid={`mobile-nav-${slugify(child.label)}`}
                                                    className={({ isActive }) =>
                                                        `py-2 text-base ${
                                                            isActive
                                                                ? "text-f1-red font-medium"
                                                                : "text-zinc-300"
                                                        }`
                                                    }
                                                >
                                                    {child.label}
                                                </NavLink>
                                            ))}
                                        </div>
                                    </div>
                                ) : (
                                    <NavLink
                                        key={item.to}
                                        to={item.to}
                                        data-testid={`mobile-nav-${slugify(item.label)}`}
                                        className={({ isActive }) =>
                                            `py-3 text-base ${
                                                isActive
                                                    ? "text-f1-red font-medium"
                                                    : "text-zinc-300"
                                            }`
                                        }
                                    >
                                        {item.label}
                                    </NavLink>
                                )
                            ))}
                            <div className="pt-3">
                                <OpenDashboardButton mobile />
                            </div>
                            <div className="pt-4 mt-2 border-t border-white/10">
                                <AuthButtons mobile />
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}

function NavDropdown({ item, active }) {
    const [isOpen, setIsOpen] = useState(false);

    const closeOnBlur = (event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
            setIsOpen(false);
        }
    };

    return (
        <div
            className="relative h-full"
            onMouseEnter={() => setIsOpen(true)}
            onMouseLeave={() => setIsOpen(false)}
            onFocus={() => setIsOpen(true)}
            onBlur={closeOnBlur}
        >
            <button
                type="button"
                data-testid={`nav-${slugify(item.label)}`}
                className={`relative inline-flex h-full items-center gap-1.5 px-3.5 text-[13px] font-medium tracking-wide transition-colors ${
                    active ? "text-white" : "text-zinc-400 hover:text-white"
                }`}
                aria-haspopup="true"
                aria-expanded={isOpen}
                onClick={() => setIsOpen((value) => !value)}
                onKeyDown={(event) => {
                    if (event.key === "Escape") {
                        setIsOpen(false);
                        event.currentTarget.blur();
                    }
                }}
            >
                <span className="relative inline-flex items-center">
                    {item.label}
                    {active && (
                        <motion.div
                            layoutId="nav-underline"
                            className="absolute -bottom-1 left-0 right-0 h-[2px] bg-f1-red"
                        />
                    )}
                </span>
                <ChevronDown
                    className={`h-3.5 w-3.5 transition-transform duration-200 ${
                        isOpen ? "rotate-180" : ""
                    }`}
                />
            </button>
            <div
                className={`absolute left-1/2 top-full z-50 w-44 -translate-x-1/2 pt-2 transition-all duration-200 ${
                    isOpen
                        ? "pointer-events-auto translate-y-0 opacity-100"
                        : "pointer-events-none translate-y-2 opacity-0"
                }`}
            >
                <div className="glass-strong overflow-hidden rounded-2xl border border-white/10 p-1 shadow-[0_22px_70px_rgba(0,0,0,0.42)]">
                    {item.items.map((child) => (
                        <NavLink
                            key={child.to}
                            to={child.to}
                            data-testid={`nav-${slugify(child.label)}`}
                            className={({ isActive }) =>
                                `flex items-center justify-between rounded-xl px-3 py-2.5 text-sm transition-colors ${
                                    isActive
                                        ? "bg-f1-red/12 text-white"
                                        : "text-zinc-400 hover:bg-white/[0.04] hover:text-white"
                                }`
                            }
                        >
                            {({ isActive }) => (
                                <>
                                    <span>{child.label}</span>
                                    <span
                                        className={`h-1.5 w-1.5 rounded-full ${
                                            isActive ? "bg-f1-red" : "bg-white/20"
                                        }`}
                                    />
                                </>
                            )}
                        </NavLink>
                    ))}
                </div>
            </div>
        </div>
    );
}

function OpenDashboardButton({ mobile = false }) {
    return (
        <Link
            to={LIVE_ROUTE}
            data-testid={mobile ? "mobile-open-dashboard-button" : "open-dashboard-button"}
            className={`group relative isolate inline-flex items-center justify-center overflow-hidden whitespace-nowrap rounded-full border border-white bg-white px-5 py-3 font-mono text-[12px] font-semibold uppercase tracking-[0.18em] text-black shadow-[0_0_22px_rgba(255,255,255,0.12)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-black hover:text-white hover:shadow-[0_0_28px_rgba(255,255,255,0.1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 ${
                mobile ? "w-full" : ""
            }`}
            aria-label="Open the live race view"
        >
            <span className="absolute inset-0 -z-10 translate-x-[-110%] bg-black transition-transform duration-300 group-hover:translate-x-0" />
            <span className="relative">Open Live</span>
        </Link>
    );
}

function AuthButtons({ mobile = false }) {
    const { isAuthenticated, user, logout } = useAuth();
    const base =
        "group relative isolate inline-flex min-w-0 items-center justify-center overflow-hidden rounded-full border px-6 py-3 font-mono text-[12px] uppercase tracking-[0.18em] transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-f1-red/70";
    const mobileClass = mobile ? "w-full" : "";

    if (isAuthenticated) {
        return (
            <div className={`flex ${mobile ? "flex-col gap-2" : "items-center gap-2"}`}>
                <Link
                    to="/replay"
                    className={`${base} ${mobileClass} border-white/10 bg-white/[0.04] text-white hover:border-f1-red/60 hover:bg-f1-red/10`}
                >
                    {user?.display_name || "Account"}
                </Link>
                <button
                    type="button"
                    onClick={logout}
                    className={`${base} ${mobileClass} border-f1-red/60 bg-transparent text-white hover:bg-f1-red`}
                >
                    Logout
                </button>
            </div>
        );
    }

    return (
        <div className={`flex ${mobile ? "flex-col gap-2" : "items-center gap-2"}`}>
            <Link
                to="/signup"
                data-testid={mobile ? "mobile-nav-sign-up" : "nav-sign-up"}
                className={`${base} ${mobileClass} border-f1-red/70 bg-transparent text-white shadow-[inset_0_0_0_1px_rgba(225,6,0,0.2)] hover:-translate-y-0.5 hover:border-white hover:bg-f1-red hover:shadow-[0_0_28px_rgba(225,6,0,0.28)]`}
            >
                <span className="absolute inset-0 -z-10 translate-y-full bg-f1-red transition-transform duration-300 group-hover:translate-y-0" />
                <span className="relative">Sign Up</span>
            </Link>
            <Link
                to="/login"
                data-testid={mobile ? "mobile-nav-login" : "nav-login"}
                className={`${base} ${mobileClass} border-f1-red bg-f1-red text-white shadow-[0_0_22px_rgba(225,6,0,0.18)] hover:-translate-y-0.5 hover:border-white hover:bg-transparent hover:text-white hover:shadow-[0_0_18px_rgba(255,255,255,0.1)]`}
            >
                <span className="absolute inset-0 -z-10 translate-y-0 bg-f1-red transition-transform duration-300 group-hover:-translate-y-full" />
                <span className="relative">Login</span>
            </Link>
        </div>
    );
}
